import styled, { css } from "styled-components"
import DateFormatter from "../DateFormatter"
import { Tags } from "../Tags/Tags"
import React, { useEffect } from "react"
import { CoverImage } from "./CoverImage"
import { addCopyButtons } from "./preview-utils"
import Link from "next/link"
import { BackButton } from "../neutral/BackButton"

const StyledContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px 0;
`

const StyledTitle = styled.h1`
  margin-top: 0;
  margin-bottom: 24px;
  font-weight: bold;
  color: ${(props) => props.theme.palette.colors.typography[1000]};
  line-height: 36px;
  font-size: 36px;
`

const StyledDate = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.palette.colors.typography[900]};
`

const StyledOrderedList = css`
  .counters {
    background: lightgray;
    text-align: left;
    padding: 5em 8em;
    margin-top: 7em;

    counter-reset: counter-name;
  }

  .section {
    position: relative;
    counter-increment: section-counter;
  }

  .section::before {
    content: counter(section-counter);
    position: absolute;
    left: -2.5em;
    top: -0.5em;
    background: white;
    width: 2em;
    height: 2em;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 3px solid gray;
    color: gray;
    box-sizing: border-box;
  }

  .counters ol {
    list-style: none;
  }

  .counters li::before {
    counter-increment: ordered-list;
    content: "list item " counter(ordered-list) ") ";
    font-size: 0.8em;
    color: $clr-red;
  }
`

const StyledPreviewText = styled.div`
  color: ${(props) => props.theme.palette.colors.typography[1000]};
  font-size: 16px;
  margin-bottom: 24px;

  * {
    color: ${(props) => props.theme.palette.colors.typography[1000]};
    font-size: 16px;
  }

  h2 {
    font-size: 20px;
  }

  code {
    background-color: ${(props) => props.theme.palette.colors.typography[1000]};
    color: ${(props) => props.theme.palette.common.white};
    padding: 2px 8px;
    border-radius: 8px;
    width: 100%;
    overflow: auto;
  }

  pre {
    position: relative;
    background-color: ${(props) => props.theme.palette.colors.typography[1000]};
    color: ${(props) => props.theme.palette.common.white};
    padding: 8px;
    border-radius: 8px;

    width: 100%;
    display: flex;
    overflow: auto;

    .copy {
      position: absolute;
      top: 0;
      right: 0;
      padding: 4px 8px;
      background-color: ${(props) => props.theme.palette.colors.purple[700]};
      color: ${(props) => props.theme.palette.common.white};
      border-radius: 8px;
      cursor: pointer;
      height: fit-content;
    }
  }

  pre:after {
    content: attr(data-before);
    position: absolute;
    top: 0;
    right: 0;
    padding: 4px 8px;
    background-color: ${(props) => props.theme.palette.colors.purple[700]};
    border-radius: 8px;
    cursor: pointer;
    height: fit-content;
  }

  a {
    border: none;
    outline: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    text-decoration: none;
    box-sizing: border-box;
    user-select: none;
    pointer-events: ${(props) => (props.disabled ? "none" : "all")};
    background-color: transparent;

    font-style: normal;
    font-weight: 600;
    text-transform: none;
    min-width: max-content;

    color: ${(props) => props.theme.palette.colors.blue[300]};
    background-color: transparent;
  }

  a:hover {
    color: ${(props) => props.theme.palette.colors.blue[400]};
  }
`

const StyledTags = styled(Tags)`
  margin-bottom: 24px;
`

const StyledCoverImage = styled(CoverImage)`
  margin-bottom: 24px;
`

export function PostPreview({ item }) {
  const { title, name, date, content, tags, coverImage } = item

  useEffect(() => {
    addCopyButtons()
  }, [])

  return (
    <StyledContainer>
      <BackButton />
      <StyledTags items={tags} />
      {coverImage && <StyledCoverImage src={coverImage} title={title} />}
      <StyledTitle>{title}</StyledTitle>
      <StyledDate>
        <DateFormatter dateString={date} />
      </StyledDate>

      <StyledPreviewText
        dangerouslySetInnerHTML={{ __html: content }}
      ></StyledPreviewText>

      <StyledTags items={tags} />
    </StyledContainer>
  )
}
